import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import type {
  CreateStructureTaskModuleFormData,
  CreateStructureTaskModuleFormProps,
} from './types';

export const CreateStructureTaskModuleForm: React.FC<CreateStructureTaskModuleFormProps> = ({
  tasks,
  clientUsers = [],
  defaultTaskId,
  initialData,
  requireVideo = true,
  submitLabel,
  onSubmit,
  onCancel,
  isLoading = false,
  uploadProgress = 0,
}) => {
  const [formData, setFormData] = useState<CreateStructureTaskModuleFormData>({
    name: '',
    description: '',
    task: defaultTaskId ?? '',
    video: null,
    required_user_ids: [],
  });
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const captureInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (defaultTaskId) {
      setFormData((prev) => ({ ...prev, task: defaultTaskId }));
    }
  }, [defaultTaskId]);

  useEffect(() => {
    if (!initialData) return;
    setFormData((prev) => ({
      ...prev,
      name: initialData.name ?? '',
      description: initialData.description ?? '',
      task: initialData.task ?? defaultTaskId ?? '',
      video: null,
      required_user_ids: initialData.required_user_ids ?? [],
    }));
  }, [initialData, defaultTaskId]);

  useEffect(() => {
    if (formData.video) {
      const url = URL.createObjectURL(formData.video);
      setVideoPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setVideoPreviewUrl(null);
    return undefined;
  }, [formData.video]);

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setFormData((prev) => ({ ...prev, video: file }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.task || (requireVideo && !formData.video)) return;
    onSubmit(formData);
  };

  const toggleRequiredUser = (userId: number) => {
    setFormData((prev) => {
      const exists = prev.required_user_ids.includes(userId);
      return {
        ...prev,
        required_user_ids: exists
          ? prev.required_user_ids.filter((id) => id !== userId)
          : [...prev.required_user_ids, userId],
      };
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="module-task">Task</Label>
        <Select
          value={formData.task}
          onValueChange={(value) => setFormData((prev) => ({ ...prev, task: value }))}
        >
          <SelectTrigger id="module-task">
            <SelectValue placeholder="Select a task..." />
          </SelectTrigger>
          <SelectContent className="max-h-[300px] overflow-y-auto">
            {tasks.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.structure_type_name} - {t.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="module-name">Module Name</Label>
        <Input
          id="module-name"
          placeholder="e.g. How to install corner bead..."
          value={formData.name}
          onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
          autoFocus
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="module-description">Description (optional)</Label>
        <Textarea
          id="module-description"
          placeholder="Brief description of this training video..."
          className="min-h-[80px] resize-none"
          value={formData.description}
          onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="module-video">Video File</Label>
        <input
          id="module-video"
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept="video/*"
          onChange={handleVideoChange}
          disabled={isLoading}
        />
        <input
          id="module-video-capture"
          ref={captureInputRef}
          type="file"
          className="hidden"
          accept="video/*"
          capture="environment"
          onChange={handleVideoChange}
          disabled={isLoading}
        />
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading}
          >
            Choose Video
          </Button>
          <Button
            type="button"
            variant="outline"
            className="md:hidden"
            onClick={() => captureInputRef.current?.click()}
            disabled={isLoading}
          >
            Take Video
          </Button>
        </div>
        {formData.video && (
          <p className="text-xs text-muted-foreground">
            Selected file: <span className="font-medium text-foreground">{formData.video.name}</span>
          </p>
        )}
        {!requireVideo && (
          <p className="text-xs text-muted-foreground">
            Optional. Upload a new file only if you want to replace the current video.
          </p>
        )}
        {videoPreviewUrl && !isLoading && (
          <video src={videoPreviewUrl} controls className="w-full rounded-md border max-h-48 mt-2" />
        )}
      </div>

      <div className="space-y-2">
        <Label>Require specific users to watch</Label>
        {clientUsers.length === 0 ? (
          <p className="text-xs text-muted-foreground">No client users available.</p>
        ) : (
          <div className="max-h-36 overflow-auto rounded-md border p-2 space-y-1">
            {clientUsers.map((user) => {
              const checked = formData.required_user_ids.includes(user.id);
              const checkboxId = `required-user-${user.id}`;
              return (
                <div key={user.id} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    id={checkboxId}
                    checked={checked}
                    onCheckedChange={() => toggleRequiredUser(user.id)}
                  />
                  <Label htmlFor={checkboxId} className="font-normal cursor-pointer">
                    {user.first_name} {user.last_name} ({user.email})
                  </Label>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {isLoading && uploadProgress > 0 && (
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Uploading video...</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-200"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={
            isLoading ||
            !formData.name.trim() ||
            !formData.task ||
            (requireVideo && !formData.video)
          }
        >
          {isLoading ? 'Saving...' : submitLabel ?? 'Upload Training Video'}
        </Button>
      </div>
    </form>
  );
};
